'use client';

import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';

import { AppLink } from '@/components/mui/app-link';
import {
  ativoPath,
  parseAtivoResourcePath,
  type AtivoAvailability,
  type AtivoCaixaSubview,
  type AtivoPrimaryTab,
} from '@/libs/ativo-route';
const TAB_HEADING_BY_TAB: Record<AtivoPrimaryTab, string> = {
  resumo: 'Resumo',
  simulacao: 'Simulação',
  cotacao: 'Cotação',
  liquidez: 'Liquidez',
  'dados-cvm': 'Dados CVM',
  dividendos: 'Dividendos',
  patrimonio: 'Patrimônio',
  caixa: 'Caixa',
  carteira: 'Carteira',
  relatorios: 'Relatórios',
};

export const resolveTickerDetailHeading = ({
  tickerLabel,
  activeTab,
  treasury,
}: {
  tickerLabel: string;
  activeTab: AtivoPrimaryTab;
  treasury: boolean;
}) => `${tickerLabel} — ${treasury && activeTab === 'cotacao' ? 'Cotação e histórico' : TAB_HEADING_BY_TAB[activeTab]}`;

export const shouldRenderReportsTab = ({ treasury, g2rsMode }: { treasury: boolean; g2rsMode: boolean }) =>
  !treasury && !g2rsMode;

type Props = {
  tickerLabel: string;
  summaryContent: ReactNode;
  simulationContent: ReactNode;
  quotationContent: ReactNode;
  liquidityContent: ReactNode;
  showLiquidityTab?: boolean;
  showCashTab?: boolean;
  showPortfolioTab?: boolean;
  cvmContent: ReactNode;
  dividendsContent: ReactNode;
  patrimonyContent: ReactNode;
  cashContent?: ReactNode;
  portfolioContent?: ReactNode;
  reportsContent: ReactNode;
  treasury?: boolean;
  g2rsMode?: boolean;
};

const useRouteState = ({
  tickerLabel,
  showLiquidityTab,
  showCashTab,
  showPortfolioTab,
}: {
  tickerLabel: string;
  showLiquidityTab: boolean;
  showCashTab: boolean;
  showPortfolioTab: boolean;
}) => {
  const availability: AtivoAvailability = useMemo(
    () => ({ showLiquidityTab, showCashTab, showPortfolioTab }),
    [showLiquidityTab, showCashTab, showPortfolioTab],
  );
  const pathname = usePathname() ?? '';
  return useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    // segments[0] === "ativo"
    // segments[1] === ticker
    // segments[2+] are tab/subview
    return {
      parsed: parseAtivoResourcePath({
        segments: [tickerLabel, ...segments.slice(2)],
        availability,
      }),
      tickerLabel,
    };
  }, [pathname, tickerLabel, availability]);
};

export function TickerDetailTabs({
  tickerLabel,
  summaryContent,
  simulationContent,
  quotationContent,
  liquidityContent,
  showLiquidityTab = true,
  showCashTab = false,
  showPortfolioTab = false,
  cvmContent,
  dividendsContent,
  patrimonyContent,
  cashContent,
  portfolioContent,
  reportsContent,
  treasury = false,
  g2rsMode = false,
}: Props) {
  const { parsed } = useRouteState({
    tickerLabel,
    showLiquidityTab,
    showCashTab,
    showPortfolioTab,
  });
  const [activeTab, setActiveTab] = useState<AtivoPrimaryTab>(parsed.tab);
  const [, setActiveCaixaSubview] = useState<AtivoCaixaSubview>(parsed.caixaSubview);
  const tabsAnchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setActiveTab(parsed.tab);
    setActiveCaixaSubview(parsed.caixaSubview);
  }, [parsed.tab, parsed.caixaSubview]);
  const pageHeading = resolveTickerDetailHeading({ tickerLabel, activeTab, treasury });

  return (
    <Box sx={{ mt: 0, px: 0, '& .MuiTabs-root': { px: 0 } }}>
      {/* The h1 stays in the DOM for SEO (one per active tab) but is not
          displayed: the tab ribbon is the first visible element. */}
      <Typography
        component='h1'
        sx={{
          border: 0,
          clip: 'rect(0, 0, 0, 0)',
          clipPath: 'inset(50%)',
          height: '1px',
          margin: '-1px',
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: '1px',
        }}
      >
        {pageHeading}
      </Typography>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Box
          sx={{
            borderBottom: '1px solid hsl(var(--border))',
            backgroundColor: 'hsl(var(--tab-ribbon))',
            borderRadius: '16px 16px 0 0',
            px: { xs: 0.75, md: 1 },
            py: 0.5,
            width: '100%',
          }}
        >
          <Tabs
            ref={tabsAnchorRef}
            value={activeTab}
            variant='standard'
            onChange={(_, value) => {
              const nextTab = value as AtivoPrimaryTab;
              setActiveTab(nextTab);
              setActiveCaixaSubview('visao-geral');
            }}
            sx={{
              px: 0,
              py: 0,
              minHeight: 'unset',
              height: 'auto',
              backgroundColor: 'transparent',
              borderRadius: 0,
              '& .MuiTabs-scroller': {
                overflow: 'visible !important',
              },
              '& .MuiTabs-flexContainer': {
                pl: 0,
                columnGap: { xs: 0.5, sm: 0.6, md: 0.45 },
                rowGap: { xs: 0.45, sm: 0.6 },
                backgroundColor: 'transparent',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignContent: 'center',
                minHeight: 'unset',
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                letterSpacing: { xs: 0.1, sm: 0.12, md: 0.1 },
                fontSize: { xs: '0.75rem', sm: '0.82rem', md: '0.8rem' },
                minHeight: { xs: 27, sm: 30 },
                py: { xs: 0.2, sm: 0.35 },
                px: { xs: 0.8, sm: 0.95, md: 0.85 },
                flex: { xs: '1 1 calc(33.333% - 8px)', sm: '0 0 auto' },
                minWidth: { xs: 0, sm: 'auto' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                border: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '1px solid hsl(var(--border) / 0.7)'
                    : '1px solid hsl(var(--border) / 0.9)',
                borderRadius: 999,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'hsl(var(--muted) / 0.55)' : 'hsl(var(--card))',
                color: (theme) =>
                  theme.palette.mode === 'dark' ? 'hsl(var(--muted-foreground))' : 'hsl(var(--muted-foreground))',
                boxShadow: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'hsl(var(--muted))' : 'hsl(var(--muted))',
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'hsl(var(--foreground))' : 'hsl(var(--foreground))',
                },
                '&.Mui-selected': {
                  color: 'hsl(var(--secondary-foreground))',
                  borderColor: 'hsl(var(--secondary))',
                  background: 'linear-gradient(135deg, hsl(var(--secondary)) 0%, hsl(var(--secondary) / 0.82) 100%)',
                  boxShadow: '0 7px 16px hsl(var(--secondary) / 0.28)',
                },
              },
            }}
          >
            <Tab label='Resumo' value='resumo' component={AppLink} href={ativoPath.tab(tickerLabel, 'resumo')} />
            {!treasury ? (
              <Tab
                label='Simulação'
                value='simulacao'
                component={AppLink}
                href={ativoPath.tab(tickerLabel, 'simulacao')}
              />
            ) : null}
            <Tab
              label={treasury ? 'Cotação e histórico' : 'Cotação'}
              value='cotacao'
              component={AppLink}
              href={ativoPath.tab(tickerLabel, 'cotacao')}
            />
            {!treasury && showLiquidityTab ? (
              <Tab
                label='Liquidez'
                value='liquidez'
                component={AppLink}
                href={ativoPath.tab(tickerLabel, 'liquidez')}
              />
            ) : null}
            {!treasury ? (
              <Tab
                label='Dados CVM'
                value='dados-cvm'
                component={AppLink}
                href={ativoPath.tab(tickerLabel, 'dados-cvm')}
              />
            ) : null}
            {!treasury ? (
              <Tab
                label='Dividendos'
                value='dividendos'
                component={AppLink}
                href={ativoPath.tab(tickerLabel, 'dividendos')}
              />
            ) : null}
            {!treasury ? (
              <Tab
                label='Patrimônio'
                value='patrimonio'
                component={AppLink}
                href={ativoPath.tab(tickerLabel, 'patrimonio')}
              />
            ) : null}
            {!treasury && showCashTab ? (
              <Tab label='Caixa' value='caixa' component={AppLink} href={ativoPath.tab(tickerLabel, 'caixa')} />
            ) : null}
            {!treasury && showPortfolioTab ? (
              <Tab
                label='Carteira'
                value='carteira'
                component={AppLink}
                href={ativoPath.tab(tickerLabel, 'carteira')}
              />
            ) : null}
            {shouldRenderReportsTab({ treasury, g2rsMode }) ? (
              <Tab
                label='Relatórios'
                value='relatorios'
                component={AppLink}
                href={ativoPath.tab(tickerLabel, 'relatorios')}
              />
            ) : null}
          </Tabs>
        </Box>
      </Stack>

      <Box
        sx={{
          mt: -1,
          border: '1px solid hsl(var(--border))',
          borderTop: 'none',
          borderRadius: '0 16px 16px 16px',
          backgroundColor: 'hsl(var(--card))',
          p: { xs: 1.5, md: 2 },
        }}
      >
        <Box sx={{ display: activeTab === 'resumo' ? 'block' : 'none' }}>{summaryContent}</Box>
        <Box sx={{ display: activeTab === 'simulacao' ? 'block' : 'none' }}>{simulationContent}</Box>
        <Box sx={{ display: activeTab === 'cotacao' ? 'block' : 'none' }}>{quotationContent}</Box>
        <Box
          sx={{
            display: showLiquidityTab && activeTab === 'liquidez' ? 'block' : 'none',
          }}
        >
          {liquidityContent}
        </Box>
        <Box sx={{ display: activeTab === 'dados-cvm' ? 'block' : 'none' }}>{cvmContent}</Box>
        <Box sx={{ display: activeTab === 'dividendos' ? 'block' : 'none' }}>{dividendsContent}</Box>
        <Box sx={{ display: activeTab === 'patrimonio' ? 'block' : 'none' }}>{patrimonyContent}</Box>
        <Box sx={{ display: activeTab === 'caixa' ? 'block' : 'none' }}>{cashContent}</Box>
        <Box sx={{ display: activeTab === 'carteira' ? 'block' : 'none' }}>{portfolioContent}</Box>
        <Box
          sx={{
            display: activeTab === 'relatorios' ? 'block' : 'none',
            minHeight: '60vh',
          }}
        >
          {reportsContent}
        </Box>
      </Box>
    </Box>
  );
}
